import { ChevronRight } from '@material-ui/icons';
import { Box, palette, Spinner, Stack, Text } from '@solvhealth/jigsaw-v1';
import moment from 'moment';
import React, { useMemo } from 'react';
import { TFuncKey, useTranslation } from 'react-i18next';
import { useLocationSlots } from './hooks/useSlots';

type Props = {
  locationId: string;
  withChevron?: boolean;
};

/**
 * Fetches and displays the next available time slot for a given location
 */
const Availability = ({ locationId, withChevron }: Props) => {
  const { data, status, isLoading } = useLocationSlots(locationId);

  const nextSlot =
    status === 'success' &&
    data?.data.slots?.[0]?.appointmentDate &&
    new Date(data?.data.slots?.[0]?.appointmentDate);

  const isToday = nextSlot && moment(nextSlot).isSame(new Date(), 'day');
  const isTomorrow = nextSlot && moment(nextSlot).isSame(moment(new Date()).add(1, 'day'), 'day');

  const { t } = useTranslation(['account']);

  const availabilityKey = useMemo<TFuncKey<['account']>>(() => {
    if (isToday) return 'account:availability.today';
    if (isTomorrow) return 'account:availability.tomorrow';
    return 'account:availability.future';
  }, [isToday, isTomorrow]);

  return (
    <Stack alignItems="center" justifyContent="flex-start" row space={0.5}>
      {isLoading && (
        <Text alignItems="center" as="div" display="flex" textColor="crunchberry">
          <Spinner color="crunchberry-light" size="xs" />{' '}
          <Box as="span" ml={1}>
            {t('account:availability.loading')}
          </Box>
        </Text>
      )}
      {data && (
        <>
          {nextSlot ? (
            <Text textColor="crunchberry">{t(availabilityKey, { date: nextSlot })}</Text>
          ) : (
            <Text muted>{t('account:availability.noAvailability')}</Text>
          )}
          {withChevron && (
            <ChevronRight
              htmlColor={nextSlot ? palette.crunchberry.base : palette.gray[500]}
              style={{ fontSize: 18 }}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default Availability;
